import React, { useEffect, useRef } from 'react';

/**
 * Scroll-linked 3D tilt — the "card lies back, then flattens as you scroll"
 * effect (Aceternity's ContainerScroll).
 *
 * Ported rather than dropped in: the original is TSX + Tailwind + framer-motion
 * on a shadcn layout, and this project is plain JSX with hand-written CSS. It
 * also runs on the eagerly-imported hero, where framer-motion's ~35 KB would
 * land straight in the entry chunk — the one thing the bundle config works
 * hardest to keep small. A scroll listener and two CSS custom properties give
 * the same picture for no bytes.
 *
 * Progress is driven by window.scrollY, not by the element's box: this sits
 * above the fold, so it is already fully on screen at rest and an
 * IntersectionObserver-style range would start it mid-way through.
 *
 *   scrollY 0 .......................... range
 *   tilt    maxTilt ...................... 0     (flat, upright)
 *
 * @param {number} maxTilt  degrees of rotateX at rest (0 = off)
 * @param {number} range    px of scrolling over which it flattens
 */
const ScrollTilt = ({
    children,
    maxTilt = 13,
    range = 460,
    className = '',
    style,
}) => {
    const wrapRef = useRef(null);

    useEffect(() => {
        const el = wrapRef.current;
        if (!el) return;

        // Anyone who has asked for less motion gets the flat, finished state.
        const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
        if (reduced.matches) {
            el.style.setProperty('--tilt', '0deg');
            el.style.setProperty('--tilt-scale', '1');
            el.style.setProperty('--tilt-lift', '0px');
            return;
        }

        // Phones get a much shallower tilt — at a narrow width the same angle
        // reads as a rendering fault rather than a deliberate perspective.
        const tiltFor = () => (window.innerWidth <= 768 ? maxTilt * 0.45 : maxTilt);

        let frame = 0;

        const paint = () => {
            frame = 0;
            const progress = Math.min(Math.max(window.scrollY / range, 0), 1);
            // easeOutCubic — most of the correction happens in the first
            // fraction of the scroll, so the form is usable almost at once.
            const eased = 1 - Math.pow(1 - progress, 3);

            el.style.setProperty('--tilt', `${(tiltFor() * (1 - eased)).toFixed(2)}deg`);
            el.style.setProperty('--tilt-scale', (0.965 + 0.035 * eased).toFixed(4));
            el.style.setProperty('--tilt-lift', `${(18 * (1 - eased)).toFixed(1)}px`);
        };

        const onScroll = () => {
            if (!frame) frame = requestAnimationFrame(paint);
        };

        paint();
        window.addEventListener('scroll', onScroll, { passive: true });
        window.addEventListener('resize', onScroll, { passive: true });

        return () => {
            if (frame) cancelAnimationFrame(frame);
            window.removeEventListener('scroll', onScroll);
            window.removeEventListener('resize', onScroll);
        };
    }, [maxTilt, range]);

    return (
        <div ref={wrapRef} className={`scroll-tilt ${className}`.trim()} style={style}>
            <div className="scroll-tilt-inner">{children}</div>
        </div>
    );
};

export default ScrollTilt;
